import React, { useContext, useEffect } from "react";

import { Stack, useToast } from "@chakra-ui/react";
import { useForm, useWatch } from "react-hook-form";
import { toastFailed, toastSuccess } from "../../../bootstrap/config";
import AdminModal from "../../AdminModal";
import PeoplePicker from "../PeoplePicker";
import TextInput from "../TextInput";
import Datepicker from "../Datepicker";
import Dropdown from "../Dropdown";
import SignatureApproval from "../SignatureApproval";
import ImageCapture from "../Photos/ImageCapture";
import { ModalContext } from "../../../contexts/ModalProvider";
import { resolveColumnVisibleWhen } from "./tableInputColumnMeta";
import { flushAllPendingSignatureStrokes } from "../../../utils/signatureUploadService";

const TableInputModal = ({
  dataValues,
  setdataValues,
  selectedDataValue,
  name,
  columns,
  onChange,
}) => {
  const { modalState, setModalState, modalScope, setModalScope } = useContext(ModalContext);
  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    reset,
  } = useForm({
    mode: "all",
  });
  const toast = useToast();
  const watchedValues = useWatch({ control });
  const equipmentWatch = useWatch({ control, name: "equipment" });

  useEffect(() => {
    if (selectedDataValue !== "") {
      reset(selectedDataValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataValue]);

  const handleAddRow = async () => {
    try {
      if (Object.keys(errors).length === 0) {
        flushAllPendingSignatureStrokes();
        const values = stripHiddenConditionalValues(getValues());
        const updatedDataValues = Array.isArray(dataValues) 
          ? [...dataValues, values]
          : [values];
        setdataValues(updatedDataValues);
        onChange({ target: { name, value: updatedDataValues } });
        toast({ ...toastSuccess, description: "Entry added" });
        reset({});
      } else {
        toast({
          ...toastFailed,
          description: "Please complete all the required fields",
        });
      }
    } catch (e: any) {
      toast({ ...toastFailed, description: e.message });
    } finally {
      setModalState("closed");
      setModalScope(undefined);
    }
  };

  const handleUpdateRow = async () => {
    try {
      if (Object.keys(errors).length === 0) {
        flushAllPendingSignatureStrokes();
        const values = stripHiddenConditionalValues(getValues());
        delete values.index;
        const updatedData = Array.isArray(dataValues) ? [...dataValues] : [];
        updatedData[selectedDataValue.index] = values;
        
        setdataValues(updatedData);
        onChange({ target: { name, value: updatedData } });
        toast({ ...toastSuccess, description: "Entry updated" });
        reset({});
        
        setModalState("closed");
        setModalScope(undefined);
      } else {
        toast({
          ...toastFailed,
          description: "Please complete all the required fields",
        });
      }
    } catch (e: any) {
      toast({ ...toastFailed, description: e.message });
    } finally {
      setModalState("closed");
      setModalScope(undefined);
    }
  };

  const handleDeleteRow = async () => {
    try {
      // Create a new array instead of mutating
      const updatedData = Array.isArray(dataValues) 
        ? dataValues.filter((p, index) => index !== selectedDataValue.index)
        : [];
      setdataValues(updatedData);
      onChange({ target: { name, value: updatedData } });
      toast({ ...toastSuccess, description: "Entry deleted" });
    } catch (e: any) {
      toast({ ...toastFailed, description: e.message });
    } finally {
      setModalState("closed");
      setModalScope(undefined);
    }
  };

  const stripHiddenConditionalValues = (raw: Record<string, unknown>) => {
    const values = { ...raw };
    for (const col of columns ?? []) {
      const vw = resolveColumnVisibleWhen(col);
      if (!vw) continue;
      const cond = String(values[vw.field] ?? "").trim();
      if (cond !== vw.equals) {
        delete values[col.value];
      }
    }
    return values;
  };

  const handleAction = async (action) => {
    if (["add", "edit"].includes(action)) {
      flushAllPendingSignatureStrokes();
      const isFormValid = await trigger();
      if (!isFormValid) {
        return toast({
          ...toastFailed,
          description: "Please complete all the required fields",
        });
      }
    }
    switch (action) {
      case "add":
        handleAddRow();
        break;
      case "edit":
        handleUpdateRow();
        break;
      case "delete":
        handleDeleteRow();
        break;
      default:
        setModalState("closed");
        setModalScope(undefined);
    }
  };
  return (
    <AdminModal
      isOpenModal={modalState !== "closed" && modalScope === name}
      modalType={modalState}
      onAction={handleAction}
      collection={"entry"}
    >
      <Stack align="flex-start" direction="column" w="full">
        {columns?.map((col, index) => {
          const { type, value, label, options, variant, placeholder, required, validations } = col;
          const visibleWhenResolved = resolveColumnVisibleWhen(col);
          if (visibleWhenResolved) {
            const fv =
              visibleWhenResolved.field === "equipment"
                ? equipmentWatch
                : watchedValues?.[visibleWhenResolved.field];
            if (String(fv ?? "").trim() !== visibleWhenResolved.equals) return null;
          }
          if (type === 'textInput') {
            return (
              <TextInput
                key={`field-${index}`}
                control={control}
                label={label}
                name={value}
                options={options}
                variant={variant}
                placeholder={placeholder}
                required={required}
                validations={validations}
              />
            )
          } else if (type === 'datepicker') {
            return (
              <Datepicker
                key={`field-${index}`}
                control={control}
                label={label}
                name={value}
                options={options}
                canSelectFutureDate={true}
                variant={variant}
                required={required}
                validations={validations}
              />
            )
          } else if (type === 'dropdown') {
            return (
              <Dropdown
                key={`field-${index}`}
                control={control}
                label={label}
                name={value}
                options={options}
                placeholder="Select"
                required={required}
                validations={validations}
              />
            )
          } else if (type === 'peoplePicker') {
            return (
              <PeoplePicker
                key={`field-${index}`}
                control={control}
                label={label}
                name={value}
                variant={variant}
                required={required}
                validations={validations}
              />
            )
          } else if (type === 'signature') {
            return (
              <SignatureApproval
                key={`field-${index}`}
                control={control}
                label={label}
                name={value}
                variant={variant}
                required={required}
                validations={validations}
              />
            )
          } else if (type === 'image') {
            const fileNameFromField =
              typeof validations?.fileNameFromField === "string"
                ? validations.fileNameFromField
                : undefined;
            return (
              <ImageCapture
                isVerticalLayout={true}
                key={`field-${index}`}
                control={control}
                label={label}
                name={value}
                required={required}
                validations={validations}
                externalFileTitle={
                  fileNameFromField
                    ? String(watchedValues?.[fileNameFromField] ?? "")
                    : undefined
                }
              />
            )
          }
          return null;
        })}
      </Stack>
    </AdminModal>
  );
};

export default TableInputModal;
